import type Parse from 'parse';

import { deadlined, logged, retryable } from '@shared/decorators';

import {
  Analysis,
  type AnalysisRepository,
  FindAnalysisFilters,
  FindByCreatorFilters,
  FindFindingForAnalysisFilters,
  Finding,
  StepTask,
} from '@telespot/domain';
import { AssetTopology, SampleTopology } from '../acquisition';
import { ParseBaseRepository } from '../../parse-base.repository';
import { AnalysisStateTopology } from './parse-analysis-state.mapper';
import { AnalysisTopology, ParseAnalysisMapper } from './parse-analysis.mapper';
import { AlgorithmTopology } from '../ai';
import { FindingTopology, ParseFindingMapper } from './parse-findings.mapper';
import { LatestVersionTopology } from '../latest-version.topology';
import { ObjectTopology } from '../../parse.topology';
import { PipelineTopology } from '../protocols';
import { UserTopology } from '../accounts';
import { OrganizationTopology } from '../organization';

export class ParseAnalysisRepository extends ParseBaseRepository implements AnalysisRepository {
  private readonly analysisMapper = new ParseAnalysisMapper(this.parse);
  private readonly findingMapper = new ParseFindingMapper(this.parse);

  public async saveAll(...analysis: Analysis[]): Promise<string[]> {
    const parseObjects = analysis.map((a) => this.analysisMapper.fromDomain(a));

    const results = await this.parse.Object.saveAll(parseObjects, this.options);

    return results.map((r) => r.id);
  }

  @retryable()
  @deadlined()
  @logged()
  public async findCreatorUserIdsInOrganizations(organizationIds: string[], userIds: string[]): Promise<string[]> {
    if (organizationIds.length === 0 || userIds.length === 0) return [];

    const ParseOrganization = this.subclasses.getSubclass(OrganizationTopology.TABLE);
    const organizationPointers = organizationIds.map((id) => ParseOrganization.createWithoutData(id));
    const userPointers = userIds.map((id) => this.parse.User.createWithoutData(id));

    const query = new this.parse.Query(AnalysisTopology.TABLE)
      .containedIn(AnalysisTopology.ORGANIZATION, organizationPointers)
      .containedIn(AnalysisTopology.CREATED_BY, userPointers)
      .select(AnalysisTopology.CREATED_BY);

    const generator = this.getTokenPaginationGenerator({ limit: 100, query, lastObjectId: undefined });

    const creatorIds = new Set<string>();
    for await (const batch of generator) {
      for (const analysis of batch) {
        const id = analysis.get(AnalysisTopology.CREATED_BY)?.id;
        if (id) creatorIds.add(id);
      }
    }

    return [...creatorIds];
  }

  public async *forEachSampleAnalysis(...sampleIds: string[]): AsyncGenerator<Analysis> {
    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);

    const parseSamples = sampleIds.map((id) => ParseSample.createWithoutData(id));

    const query = new this.parse.Query(AnalysisTopology.TABLE).containedIn(AnalysisStateTopology.SAMPLE, parseSamples);

    const generator = this.getTokenPaginationGenerator({ query, limit: 100 });

    for await (const items of generator) {
      for (const item of items) yield this.analysisMapper.toDomain(item);
    }
  }

  @retryable()
  @deadlined()
  @logged()
  public async findForSample(sampleId: string): Promise<Analysis[]> {
    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);

    const parseAnalysis = await new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.SAMPLE, ParseSample.createWithoutData(sampleId))
      .find(this.options);

    return parseAnalysis.map((pa) => this.analysisMapper.toDomain(pa));
  }

  public async deleteAllAnalysis(analysisIds: string[]): Promise<void> {
    const ParseAnalysis = this.subclasses.getSubclass(AnalysisTopology.TABLE);

    const parseAnalysis = analysisIds.map((id) => ParseAnalysis.createWithoutData(id));

    await this.parse.Object.destroyAll(parseAnalysis, this.options);
  }

  public async *find(filters: FindAnalysisFilters): AsyncGenerator<Analysis[]> {
    const query = this.getQueryFrom(filters);

    const generator = this.getTokenPaginationGenerator({ query, limit: 100 });

    for await (const items of generator) yield items.map((i) => this.analysisMapper.toDomain(i));
  }

  public async *forEach(filters: FindAnalysisFilters): AsyncGenerator<Analysis> {
    for await (const batch of this.find(filters)) {
      for (const analysis of batch) yield analysis;
    }
  }

  public async *getFindings(filters: FindAnalysisFilters, creatorId: string): AsyncGenerator<Finding[]> {
    for await (const batch of this.find(filters)) {
      const findingQuery = new this.parse.Query(FindingTopology.TABLE)
        .containedIn(
          FindingTopology.ANALYSIS,
          batch?.map((an) => this.analysisMapper.fromDomain(an))
        )
        .include(FindingTopology.ANALYSIS)
        .equalTo(FindingTopology.CREATOR_ID, creatorId);

      const generator = this.getTokenPaginationGenerator({ query: findingQuery, limit: 100 });

      for await (const items of generator) yield items.map((i) => this.findingMapper.toDomain(i));
    }
  }

  public async *findForAssets(assetIds: string[], filters: FindAnalysisFilters): AsyncGenerator<Analysis[]> {
    const ParseAsset = this.subclasses.getSubclass(AssetTopology.TABLE);

    const assets = assetIds.map((id) => ParseAsset.createWithoutData(id));

    const query = this.getQueryFrom(filters).containedIn(AnalysisTopology.ASSET, assets);

    const generator = this.getTokenPaginationGenerator({ query, limit: 100 });

    for await (const items of generator) yield items.map((i) => this.analysisMapper.toDomain(i));
  }

  public async *findLatestFindingsFromAnalyses(analysisIds: string[], filters: FindFindingForAnalysisFilters) {
    if (analysisIds.length === 0) return;

    const { analyzerEntity, analyzerId } = filters;
    const ParseAnalysis = this.subclasses.getSubclass(AnalysisTopology.TABLE);
    const analysis = analysisIds.map((id) => ParseAnalysis.createWithoutData(id));

    const analysisFindingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .containedIn(FindingTopology.ANALYSIS, analysis)
      .equalTo(FindingTopology.CREATOR_ID, analyzerId)
      .equalTo(FindingTopology.CREATOR_ENTITY, analyzerEntity);

    const latestVersionQuery = new this.parse.Query(LatestVersionTopology.TABLE)
      .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
      .matchesKeyInQuery(LatestVersionTopology.VERSION_ID, ObjectTopology.ID, analysisFindingsQuery);

    const findingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionQuery)
      .include([FindingTopology.ANALYSIS, FindingTopology.PIPELINE_STEP]);

    const generator = this.getTokenPaginationGenerator({ query: findingsQuery, limit: 100 });

    for await (const items of generator) yield items.map((i) => this.findingMapper.toDomain(i));
  }

  public async save(analysis: Analysis): Promise<string> {
    const parseAnalysis = this.analysisMapper.fromDomain(analysis);

    const { id } = await parseAnalysis.save(null, this.options);

    return id;
  }

  private getQueryFrom(filters: FindAnalysisFilters): Parse.Query {
    const { sampleId, analyzerEntity, analyzerId, pipelineIds } = filters;

    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);
    const ParseAlgorithm = this.subclasses.getSubclass(AlgorithmTopology.TABLE);

    const sample = ParseSample.createWithoutData(sampleId);

    const isAI = analyzerEntity === 'algorithm';
    const ParseAnalyzerEntity = isAI ? ParseAlgorithm : this.parse.User;

    const createdByKey = isAI ? 'algorithm' : 'createdBy';
    const createdBy = analyzerId ? ParseAnalyzerEntity.createWithoutData(analyzerId) : undefined;

    const query = new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.SAMPLE, sample)
      .exists(AnalysisTopology.ASSET)
      .descending(ObjectTopology.UPDATED_AT);

    if (createdBy) {
      query.equalTo(createdByKey, createdBy);
      query.exists(createdByKey);
    }

    if (pipelineIds) {
      const pipelinesQuery = new this.parse.Query(PipelineTopology.TABLE).containedIn(ObjectTopology.ID, pipelineIds);
      query.matchesQuery(AnalysisTopology.PIPELINE, pipelinesQuery);
    }

    return query;
  }

  @retryable()
  @deadlined()
  @logged()
  public async getByUUID(uuid: string): Promise<Analysis | undefined> {
    const parseAnalysis = await new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.UUID, uuid)
      .first(this.options);

    return parseAnalysis ? this.analysisMapper.toDomain(parseAnalysis) : undefined;
  }

  @retryable()
  @deadlined()
  @logged()
  public async getById(id: string): Promise<Analysis | undefined> {
    const parseAnalysis = await new this.parse.Query(AnalysisTopology.TABLE).get(id, this.options);

    return this.analysisMapper.toDomain(parseAnalysis);
  }

  @retryable()
  @deadlined()
  @logged()
  public async findByCreator(filters: FindByCreatorFilters): Promise<Analysis | undefined> {
    const { sampleId, assetId, creatorEntity, creatorId, pipelineId } = filters;

    const creatorTable = creatorEntity === 'algorithm' ? AlgorithmTopology.TABLE : UserTopology.TABLE;
    const creatorKey = creatorEntity === 'algorithm' ? AnalysisTopology.ALGORITHM : AnalysisTopology.CREATED_BY;

    const sampleRef = this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(sampleId);
    const assetRef = this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(assetId);
    const pipelineRef = this.subclasses.getSubclass(PipelineTopology.TABLE).createWithoutData(pipelineId);
    const creatorRef = this.subclasses.getSubclass(creatorTable).createWithoutData(creatorId);

    const query = new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.SAMPLE, sampleRef)
      .equalTo(AnalysisTopology.ASSET, assetRef)
      .equalTo(AnalysisTopology.PIPELINE, pipelineRef)
      .equalTo(creatorKey, creatorRef)
      .descending(ObjectTopology.CREATED_AT);

    const analysis = await query.first(this.options);

    return analysis ? this.analysisMapper.toDomain(analysis) : undefined;
  }
}
